"use client";

import { planCheckoutTruckloads } from "@shared/products/checkout";
import { shipmentTruck, shipmentTruckOptions } from "@/lib/checkout/shipment-capacity";
import type { ResolvedTruckCapacity } from "@shared/products/truck-capacity";

import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { z } from "zod";
import type { Id } from "@convex/_generated/dataModel";
import type { CheckoutShipment } from "@convex/schemas/validators/checkoutShipment";
import { defaultDeliveryWindow } from "@/lib/checkout/delivery-window";

const draftValidator = z.object({
  shipmentId: z.string(),
  quantity: z.number(),
  truckType: z.string().optional(),
  truckCapacity: z.number().optional(),
  line1: z.string(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  country: z.enum(["US", "CA"]),
  deliveryDate: z.string(),
  deliveryWindowEnd: z.string(),
  hasDockOnSite: z.boolean(),
});
const plansValidator = z.record(z.string(), z.array(draftValidator));
export type ShipmentDraft = z.infer<typeof draftValidator>;
export interface ShipmentProduct {
  productId: Id<"products">;
  productSlug?: string | null;
  quantity: number;
  minQuantity?: number;
  truckCapacity: number;
  truckOptions?: ResolvedTruckCapacity[];
  leadTimeDays: number;
}

export function defaultShipments(product: ShipmentProduct): ShipmentDraft[] {
  const deliveryWindow = defaultDeliveryWindow(product.leadTimeDays);
  return planCheckoutTruckloads(
    product.quantity,
    shipmentTruckOptions(product),
  ).map((load, index) => ({
    shipmentId: `${product.productId}-${index}`,
    ...load,
    line1: "",
    city: "",
    state: "",
    postalCode: "",
    country: "US",
    deliveryDate: deliveryWindow.start,
    deliveryWindowEnd: deliveryWindow.end,
    hasDockOnSite: true,
  }));
}

const memoryPlans = new Map<string, string>();
const changeEvent = "repackify-shipment-plan-changed";
function subscribe(callback: () => void) {
  window.addEventListener(changeEvent, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(changeEvent, callback);
    window.removeEventListener("storage", callback);
  };
}
function readRaw(key: string) {
  try {
    return sessionStorage.getItem(key) ?? "{}";
  } catch {
    return memoryPlans.get(key) ?? "{}";
  }
}
function writeRaw(key: string, value: string) {
  memoryPlans.set(key, value);
  try {
    sessionStorage.setItem(key, value);
  } catch {}
  window.dispatchEvent(new Event(changeEvent));
}
function parsePlans(raw: string | null) {
  try {
    return plansValidator.parse(JSON.parse(raw ?? "{}"));
  } catch {
    return {};
  }
}
const PLAN_KEY_PREFIX = "repackify-shipment-plan:";

export function clearShipmentPlansFor(
  productIds: string[],
  ownerId: string | null,
) {
  if (typeof window === "undefined" || productIds.length === 0) return;
  const keys = new Set<string>([`${PLAN_KEY_PREFIX}guest`]);
  if (ownerId) keys.add(`${PLAN_KEY_PREFIX}${ownerId}`);
  for (const key of keys) {
    const plans = parsePlans(readRaw(key));
    const remaining = Object.fromEntries(
      Object.entries(plans).filter(([productId]) => !productIds.includes(productId)),
    );
    writeRaw(key, JSON.stringify(remaining));
  }
}

export function seedShipmentPlans(
  ownerId: string,
  productIds: string[],
  shipments: CheckoutShipment[],
) {
  if (typeof window === "undefined") return;
  clearShipmentPlansFor(productIds, ownerId);
  if (shipments.length === 0) return;
  const key = `${PLAN_KEY_PREFIX}${ownerId}`;
  const seeded: Record<string, ShipmentDraft[]> = {};
  for (const { productId, ...draft } of shipments) {
    seeded[productId] = [
      ...(seeded[productId] ?? []),
      { ...draft, deliveryWindowEnd: draft.deliveryWindowEnd ?? draft.deliveryDate },
    ];
  }
  writeRaw(key, JSON.stringify({ ...parsePlans(readRaw(key)), ...seeded }));
}

export function useShipmentPlan(
  products: ShipmentProduct[],
  ownerId: string | null | undefined,
) {
  const storageKey = `repackify-shipment-plan:${ownerId ?? "guest"}`;
  const raw = useSyncExternalStore(
    subscribe,
    useCallback(() => readRaw(storageKey), [storageKey]),
    () => null,
  );
  const plans = useMemo(() => parsePlans(raw), [raw]);
  useEffect(() => {
    if (!ownerId) return;
    const guest = parsePlans(readRaw("repackify-shipment-plan:guest"));
    if (!Object.keys(guest).length) return;
    writeRaw(
      storageKey,
      JSON.stringify({ ...parsePlans(readRaw(storageKey)), ...guest }),
    );
    writeRaw("repackify-shipment-plan:guest", "{}");
  }, [storageKey, ownerId]);
  const shipments: CheckoutShipment[] = products.flatMap((product) =>
    (plans[product.productId] ?? defaultShipments(product)).map((draft) => ({
      ...draft,
      truckType: shipmentTruck(product, draft)?.truckType ?? draft.truckType,
      truckCapacity: shipmentTruck(product, draft)?.capacity ?? draft.truckCapacity,
      productId: product.productId,
    })),
  );
  const update = (productId: string, drafts: ShipmentDraft[]) =>
    writeRaw(
      storageKey,
      JSON.stringify({
        ...parsePlans(readRaw(storageKey)),
        [productId]: drafts,
      }),
    );
  const initialize = (productId: string, drafts: ShipmentDraft[]) => {
    if (
      !Object.prototype.hasOwnProperty.call(
        parsePlans(readRaw(storageKey)),
        productId,
      )
    )
      update(productId, drafts);
  };
  return { ready: raw !== null, shipments, update, initialize };
}
